import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Stack, Typography, Button, Paper, alpha, useTheme, IconButton, Tooltip
} from '@mui/material';
import { useAuth } from '../util/auth';
import { useDarkMode } from '../util/theme';
import styled from '@emotion/styled';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import HelpIcon from '@mui/icons-material/Help';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import PeopleIcon from '@mui/icons-material/People';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';

const ModernSidebar = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  padding: '24px',
  background: theme.palette.background.paper,
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
  margin: 0,
  boxSizing: 'border-box',
}));

const ModernNavItem = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'active',
})(({ theme, active }) => ({
  width: '100%',
  justifyContent: 'flex-start',
  padding: theme.spacing(1.25, 2),
  borderRadius: 6,
  color: active ? theme.palette.text.primary : theme.palette.text.secondary,
  backgroundColor: active ? alpha(theme.palette.primary.main, 0.15) : 'transparent',
  border: '1px solid transparent',
  fontWeight: active ? 600 : 500,
  textTransform: 'none',
  fontSize: '0.9375rem',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, active ? 0.2 : 0.1),
    color: theme.palette.text.primary,
  },
  '& .MuiSvgIcon-root': {
    fontSize: 20,
    marginRight: theme.spacing(1.5),
    color: active ? theme.palette.primary.main : theme.palette.text.secondary,
  },
  transition: 'all 0.2s ease',
}));

const SideMenu = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user, isAdmin } = useAuth();
  const { value: isDarkMode, toggle: toggleTheme } = useDarkMode();

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <ModernSidebar>
      {/* Logo and branding */}
      <Stack spacing={1} sx={{ mb: 0, pb: '20px', borderBottom: `1px solid ${alpha(theme.palette.text.primary, 0.12)}` }}>
        <Typography variant="h5" sx={{
          fontWeight: 700,
          color: theme.palette.text.primary,
          letterSpacing: '-0.5px',
        }}>
          BonusWatcher
        </Typography>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 500, fontSize: '0.8125rem' }}>
          Track your bank bonuses
        </Typography>
      </Stack>

      {/* Navigation items */}
      <Stack spacing={1}>
        <ModernNavItem
          active={location.pathname === '/bonus-watcher'}
          onClick={() => navigate('/bonus-watcher')}
        >
          <MonetizationOnIcon />
          Bonus Watcher
        </ModernNavItem>
        <ModernNavItem 
          active={location.pathname === '/leaderboard'}
          onClick={() => navigate('/leaderboard')}
        >
          <LeaderboardIcon />
          Leaderboard
        </ModernNavItem>
        <ModernNavItem
          active={location.pathname === '/friends'}
          onClick={() => navigate('/friends')}
        >
          <PeopleIcon />
          Friends
        </ModernNavItem>
        {isAdmin && (
          <ModernNavItem
            active={location.pathname === '/admin'}
            onClick={() => navigate('/admin')}
          >
            <AdminPanelSettingsIcon />
            Admin Panel
          </ModernNavItem>
        )}
        <ModernNavItem
          active={location.pathname === '/help'}
          onClick={() => navigate('/help')}
        >
          <HelpIcon />
          Help
        </ModernNavItem>
        <ModernNavItem 
          active={location.pathname === '/profile'}
          onClick={() => navigate('/profile')}
        >
          <AccountCircleIcon />
          Profile
        </ModernNavItem>
      </Stack>

      {/* User stats */}
      <Box sx={{ mt: 'auto', pt: '20px' }}>
        <Paper sx={{
          p: 2,
          background: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.6) : alpha(theme.palette.background.default, 0.5),
          borderRadius: '8px',
          border: `1px solid ${alpha(theme.palette.text.primary, 0.12)}`,
          boxShadow: 'none',
        }}>
          <Stack spacing={2}>
            <Typography variant="subtitle2" sx={{
              color: theme.palette.text.secondary,
              fontWeight: 600,
              fontSize: '0.85rem',
            }}>
              {user?.email}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Tooltip title={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"} arrow>
                <IconButton
                  onClick={toggleTheme}
                  sx={{
                    color: theme.palette.primary.main,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    borderRadius: '6px',
                    width: '40px',
                    height: '40px',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.2),
                      transform: 'scale(1.05)',
                    }
                  }}
                >
                  {isDarkMode ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
              <ModernNavItem
                onClick={handleLogout}
                sx={{
                  color: '#DC2626',
                  border: 'none',
                  flex: 1,
                  '&:hover': {
                    backgroundColor: alpha('#DC2626', 0.1),
                  },
                  '& .MuiSvgIcon-root': {
                    color: '#DC2626',
                  }
                }}
              >
                <LogoutIcon />
                Logout
              </ModernNavItem>
            </Stack>
          </Stack>
        </Paper>
      </Box>
    </ModernSidebar>
  );
};

export default SideMenu; 